import { Box, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import FileUploadWithButton from "./shared/FileUploadWithButton";
import EditableJsonViewer from "./EditableJsonViewer";
import BotTypeTabs from "./BotTypesTab";
import BotRunStatusTabs from "./BotRunStatusTabs";
import TacoBellDashboard from "../TacoBellDashboard";
import { PostStartBot, PostUploadDailySalesReportExcel, PostStartTacoBellDelaget } from "../apis/post";
import { GetBotStatusData, GetClientEmails } from "../apis/get";
import { BotType } from "../../utilities/type";
import { GetSuccessMsg } from "../apis/get";
import { ApiRequest } from "../../services/api";
import { toast } from "react-hot-toast";
const LandingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<
    "upload" | "json" | "client-emails" | "bot-status" | "tacobell-dashboard"
  >("upload");

  const [selectedBotType, setSelectedBotType] = useState<BotType | null>(null);
  const [clientEmails, setClientEmails] = useState<Record<string, any>>({});
  const [botStatusData, setBotStatusData] = useState<Record<string, any>>({});

  // 🔹 Date states for bots
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [predLoading, setPredLoading] = useState(false);

  // 🔹 Upload Excel File
  const handleSubmit = async (file: File, botType: BotType) => {
    if (!file) {
      alert("Please select a file to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("file1", file);
    formData.append("botType", botType);

    try {
      const response = await PostUploadDailySalesReportExcel(formData);
      console.log("✅ File Upload Response:", response);
      window.location.reload();
    } catch (error) {
      console.error("❌ Error uploading file:", error);
    }
  };

  // 🔹 Start Bot handler
  const handleStartBot = async (botType: BotType, cities: string[]) => {
    console.log("Bot selected:", botType);
    setSelectedBotType(botType);

    if (botType === BotType.ActiveCrewStaff) {
      console.log("Selected Dates:", { startDate, endDate });
       try {
            const data = await GetSuccessMsg(startDate, endDate);
            console.log("API Response:", data);
            //setCrewData(data); // save data to state to render in UI
          } catch (error) {
            console.error("Error fetching crew data:", error);
          }
      return;
    }

    if (botType === BotType.TacoBellDelaget) {
      handleTacoBellDelagetRun();
      return;
    }

    if (botType === BotType.PredictiveScheduling) {
      handlePredictiveRun();
      return;
    }

    try {
      const response = await PostStartBot({ botType, cities });
      console.log("✅ Bot Start Response:", response);
    } catch (error) {
      console.error("❌ Error starting bot:", error);
    }
  };

  const formatDateToMMDDYYYY = (dateStr: string) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${month}/${day}/${year}`;
  };

  const handlePredictiveRun = async () => {
    if (!startDate || !endDate) {
      toast.error("Please select a date range.");
      return;
    }
    try {
      setPredLoading(true);
      const payload = {
        startDate: formatDateToMMDDYYYY(startDate),
        fromdate: formatDateToMMDDYYYY(startDate),
        fromDate: formatDateToMMDDYYYY(startDate),
        toDate: formatDateToMMDDYYYY(endDate)
      };
      const response = await ApiRequest('POST', 'predictive/run', payload as any);
      if (response && (response.success || response.status === 'success')) {
        toast.success(`🤖 Predictive Analysis started: ${payload.fromDate} → ${payload.toDate}`);
      }
    } catch (err) {
      console.error("Error starting predictive bot:", err);
    } finally {
      setPredLoading(false);
    }
  };

  const handleTacoBellDelagetRun = async () => {
    try {
      setPredLoading(true);
      const response = await PostStartTacoBellDelaget();
      if (response && (response.success || response.status === 'success' || response.message)) {
        toast.success(response.message || "🌮 Taco Bell Delaget Bot started successfully!");
      }
    } catch (err) {
      console.error("Error starting Taco Bell Delaget bot:", err);
      toast.error("Failed to start Taco Bell Delaget bot.");
    } finally {
      setPredLoading(false);
    }
  };

  // 🔹 Handle BotType change
  const handleBotTypeChange = (botType: BotType) => {
    console.log("BotType changed:", botType);
    setSelectedBotType(botType);
  };

  // 🔹 Fetch Client Emails & Bot Status
  useEffect(() => {
    (async () => {
      try {
        const [emails, status] = await Promise.all([
          GetClientEmails(),
          GetBotStatusData(),
        ]);
        if (emails.status) setClientEmails(emails.data || {});
        if (status.status) setBotStatusData(status.data || {});
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  // 🔹 Tab button component
  const TabButton = ({
    id,
    label,
  }: {
    id: typeof activeTab;
    label: string;
  }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`py-2 px-4 font-semibold text-lg rounded-t-lg transition-all duration-200
        ${
          activeTab === id
            ? "bg-gray-900 text-yellow-400 border-b-2 border-yellow-400"
            : "text-gray-300 hover:text-yellow-300"
        }`}
    >
      {label}
    </button>
  );

  return (
    <div className="max-w-6xl mx-auto p-8 bg-black bg-opacity-40 backdrop-blur rounded-2xl shadow-2xl text-white mt-8 mb-8">
      <h2 className="text-2xl font-bold text-yellow-300 text-center mb-8">
        Upload Excel File for Processing
      </h2>

      {/* 🔸 Tabs Navigation */}
      <div className="flex mb-6 gap-6 border-b-2 border-yellow-300 justify-center">
        <TabButton id="upload" label="Upload File" />
        <TabButton id="json" label="Email Footer JSON" />
        <TabButton id="client-emails" label="Client Emails" />
        <TabButton id="bot-status" label="Bot Run Status" />
      </div>

      {/* 🔸 Tab Content */}
      <div className="mt-6">
        {activeTab === "upload" && (
          <div className="bg-gray-800 p-8 rounded-2xl shadow-xl text-white space-y-6">
            {/* 👇 Dates at top inside main container */}
            {(selectedBotType === BotType.ActiveCrewStaff || selectedBotType === BotType.PredictiveScheduling) && (
              <div className="flex flex-col sm:flex-row justify-between items-center gap-6 mb-6">
                <div className="flex flex-col w-full sm:w-1/2">
                  <label className="text-yellow-300 mb-1 text-sm">
                    Start Date
                  </label>
                  <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  onClick={(e) => e.currentTarget.showPicker()}
                  className="w-full bg-[#111827] border border-gray-700 rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 transition duration-200"
                />
                </div>

                <div className="flex flex-col w-full sm:w-1/2">
                  <label className="text-yellow-300 mb-1 text-sm">
                    End Date
                  </label>
                  <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  onClick={(e) => e.currentTarget.showPicker()}
                  className="w-full bg-[#111827] border border-gray-700 rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 transition duration-200"
                />
                </div>
              </div>
            )}

            {/* Upload + Bot Controls below */}
            <FileUploadWithButton
              onFileUpload={handleSubmit}
              onStartBot={handleStartBot}
              onBotTypeChange={handleBotTypeChange}
              buttonLabel="Select Excel File"
              fileTypes={[".xlsx", ".xls", ".csv"]}
            />
          </div>
        )}

        {activeTab === "json" && <EditableJsonViewer />}
        {activeTab === "client-emails" && <BotTypeTabs data={clientEmails} />}
        {activeTab === "bot-status" && (
          <BotRunStatusTabs initialData={botStatusData} />
        )}
      </div>

      <style>{`
        input[type="date"]::-webkit-calendar-picker-indicator {
          filter: invert(1);
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
